import { useMemo, useState } from 'react';
import { Search, ChevronRight, Eye, EyeOff, Trash2, GripVertical, Users } from 'lucide-react';

const UNGROUPED = '—';

const meta = (card) => [
  card.suggested_window === 'any' ? 'Any' : card.suggested_window,
  card.duration_minutes ? `${card.duration_minutes} min` : 'open length',
  card.adopted_count ? null : (card.source?.label ? 'gated' : 'ungated'),
].filter(Boolean).join(' · ');

/**
 * The left pane: his whole library on one screen, grouped by family and sub-group, each group
 * collapsed until he opens it. Rows are draggable to reorder, tickable for bulk changes, and
 * clicking one opens it in the editor beside — never below.
 */
export const RitualCardList = ({
  cards, selectedId, ticked, onSelect, onTick, onDelete, onReorder,
}) => {
  const [query, setQuery] = useState('');
  const [closed, setClosed] = useState({});      // key → true when collapsed
  const [dragging, setDragging] = useState(null);

  const term = query.trim().toLowerCase();
  const matches = useMemo(() => cards.filter((c) => !term ||
    `${c.title} ${c.description} ${c.category} ${c.subcategory}`.toLowerCase().includes(term)),
  [cards, term]);

  // family → sub-group → cards, in the order they already sit in
  const families = useMemo(() => {
    const tree = new Map();
    matches.forEach((card) => {
      const family = (card.category || '').trim() || UNGROUPED;
      const group = (card.subcategory || '').trim() || UNGROUPED;
      if (!tree.has(family)) tree.set(family, new Map());
      const groups = tree.get(family);
      if (!groups.has(group)) groups.set(group, []);
      groups.get(group).push(card);
    });
    return [...tree.entries()].sort(([a], [b]) =>
      (a === UNGROUPED) - (b === UNGROUPED) || a.localeCompare(b));
  }, [matches]);

  const isClosed = (key) => (term ? false : closed[key] ?? true);
  const toggle = (key) => setClosed((prev) => ({ ...prev, [key]: !(prev[key] ?? true) }));

  const drop = (target) => {
    if (!dragging || dragging === target.id) return;
    const ids = cards.map((c) => c.id).filter((id) => id !== dragging);
    ids.splice(ids.indexOf(target.id), 0, dragging);
    setDragging(null);
    onReorder(ids);
  };

  const row = (card) => (
    <div key={card.id} draggable
         onDragStart={() => setDragging(card.id)}
         onDragOver={(e) => e.preventDefault()}
         onDrop={() => drop(card)}
         onDragEnd={() => setDragging(null)}
         className={`group flex items-center gap-2 rounded-lg border px-2 py-1.5 cursor-pointer
                     transition-colors ${
           card.id === selectedId
             ? 'border-amber-400/70 bg-amber-400/10'
             : 'border-transparent hover:border-slate-600 hover:bg-slate-800/40'}
           ${dragging === card.id ? 'opacity-40' : ''}`}
         onClick={() => onSelect(card)}
         data-testid={`ritual-card-row-${card.id}`}>
      <GripVertical className="w-3.5 h-3.5 shrink-0 text-slate-600 group-hover:text-slate-400" />
      <input type="checkbox" checked={ticked.includes(card.id)} className="accent-amber-400"
             onClick={(e) => e.stopPropagation()}
             onChange={(e) => onTick(card.id, e.target.checked)}
             data-testid={`tick-ritual-card-${card.id}`} />
      <button className="flex-1 min-w-0 text-left" data-testid={`edit-ritual-card-${card.id}`}>
        <span className="block truncate text-[13px] text-white">{card.title}</span>
        <span className="block text-[10px] text-slate-500">
          {meta(card)}
          {card.adopted_count > 0 && (
            <span className="ml-1 text-cyan-300/80">
              · {card.adopted_count} member{card.adopted_count === 1 ? '' : 's'}
            </span>
          )}
        </span>
      </button>
      {card.active
        ? <Eye className="w-3.5 h-3.5 shrink-0 text-emerald-300/70" title="Live" />
        : <EyeOff className="w-3.5 h-3.5 shrink-0 text-slate-500" title="Hidden" />}
      <button onClick={(e) => { e.stopPropagation(); onDelete(card); }}
              className="shrink-0 text-slate-600 hover:text-rose-300 transition-colors"
              data-testid={`delete-ritual-card-${card.id}`}>
        <Trash2 className="w-3.5 h-3.5" />
      </button>
    </div>
  );

  const header = (key, label, count, depth) => (
    <button onClick={() => toggle(key)}
            className={`flex w-full items-center gap-1.5 rounded-lg px-2 py-1.5 text-left
                        transition-colors hover:bg-slate-800/50 ${depth ? 'pl-5' : ''}`}
            data-testid={`ritual-group-${key}`}>
      <ChevronRight className={`w-3.5 h-3.5 text-slate-500 transition-transform
                                ${isClosed(key) ? '' : 'rotate-90'}`} />
      <span className={depth ? 'text-[11px] text-slate-300' : 'text-xs font-semibold text-white'}>
        {label}
      </span>
      <span className="text-[10px] text-slate-500">{count}</span>
    </button>
  );

  return (
    <div className="space-y-2" data-testid="ritual-card-list">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500" />
        <input value={query} onChange={(e) => setQuery(e.target.value)}
               placeholder={`Search ${cards.length} cards`}
               className="w-full rounded-lg border border-slate-700 bg-slate-900 pl-9 pr-3 py-2
                          text-sm text-white placeholder-slate-500 focus:outline-none
                          focus:border-amber-400/70"
               data-testid="ritual-card-search" />
      </div>

      <div className="max-h-[34rem] overflow-y-auto pr-1 space-y-0.5">
        {families.length === 0 && (
          <p className="py-8 text-center text-xs text-slate-500" data-testid="no-ritual-cards">
            {term ? 'Nothing matches that.' : 'No cards yet. Draft some from a document.'}
          </p>
        )}

        {families.map(([family, groups]) => {
          const total = [...groups.values()].reduce((n, list) => n + list.length, 0);
          const single = groups.size === 1 && groups.has(UNGROUPED);
          return (
            <div key={family}>
              {header(family, family === UNGROUPED ? 'Unfiled' : family, total, false)}
              {!isClosed(family) && (
                <div className="space-y-0.5 pl-3">
                  {single
                    ? groups.get(UNGROUPED).map(row)
                    : [...groups.entries()]
                        .sort(([a], [b]) => (a === UNGROUPED) - (b === UNGROUPED) || a.localeCompare(b))
                        .map(([group, list]) => (
                          <div key={group}>
                            {header(`${family}/${group}`,
                                    group === UNGROUPED ? 'No group' : group, list.length, true)}
                            {!isClosed(`${family}/${group}`) && (
                              <div className="space-y-0.5 pl-3">{list.map(row)}</div>
                            )}
                          </div>
                        ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <p className="flex items-center gap-1.5 text-[10px] text-slate-500">
        <Users className="w-3 h-3" /> Member counts are how many people are carrying that card.
        Drag a row to reorder.
      </p>
    </div>
  );
};

export default RitualCardList;
